import React from 'react';
import { Card } from '@/components/ui/Card';
import { SubtitleText } from '@/components/ui/SubtitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Button } from '@/components/ui/Button';
import { useAppointmentHistory } from '@/hooks/useAppointmentHistory';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';

interface AppointmentHistoryCardProps {
  dietitianId: string;
  patientId: string;
  pageSize?: number;
  title?: string;
}

export const AppointmentHistoryCard: React.FC<AppointmentHistoryCardProps> = ({
  dietitianId,
  patientId,
  pageSize = 10,
  title = 'Appointment History',
}) => {
  const { isDark } = useTheme();
  const { items, loading, error, page, pageSize: currentPageSize, totalItems, goToPage, refresh } =
    useAppointmentHistory({ dietitianId, patientId, pageSize });

  const totalPages = Math.max(1, Math.ceil(totalItems / currentPageSize));

  const handlePrev = async () => {
    if (page > 1) {
      await goToPage(page - 1);
    }
  };

  const handleNext = async () => {
    if (page < totalPages) {
      await goToPage(page + 1);
    }
  };

  return (
    <Card
      className={cn(
        'p-6 transition-shadow duration-200',
      )}
    >
      <div className="flex items-center justify-between mb-4">
        <SubtitleText className="font-semibold">{title}</SubtitleText>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refresh()} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-12 rounded animate-pulse"
              style={{
                background: isDark
                  ? 'linear-gradient(90deg, rgba(64,64,64,0.6), rgba(88,88,88,0.6), rgba(64,64,64,0.6))'
                  : 'linear-gradient(90deg, rgba(229,231,235,0.8), rgba(243,244,246,0.8), rgba(229,231,235,0.8))',
              }}
            />
          ))}
        </div>
      ) : error ? (
        <div
          className={cn(
            'text-center p-4 rounded',
            isDark ? 'bg-error-900/25 text-error-200' : 'bg-error-50 text-error-700'
          )}
        >
          <BodyText>{error}</BodyText>
          <Button className="mt-3" variant="primary" size="sm" onClick={() => refresh()}>
            Retry
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div
          className={cn(
            'text-center p-6 rounded',
            isDark ? 'bg-secondary-900/50 text-secondary-200' : 'bg-secondary-50 text-secondary-700'
          )}
        >
          <BodyText variant="muted">No appointments found.</BodyText>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((appt) => {
            const scheduled = new Date(appt.scheduledAt);
            const isPast = scheduled.getTime() < Date.now();
            return (
              <div
                key={appt.id}
                className={cn(
                  'rounded p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 transition-shadow duration-200 hover:shadow-lg',
                )}
              >
                <div>
                  <BodyText
                    className={cn(
                      'font-semibold',
                      isDark ? 'text-secondary-100' : 'text-secondary-900'
                    )}
                  >
                    {appt.type || 'Appointment'} • {appt.status}
                  </BodyText>
                  <BodyText
                    variant="muted"
                    size="sm"
                    className={cn(isDark ? 'text-secondary-300' : 'text-secondary-700')}
                  >
                    {scheduled.toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                    {appt.location ? ` • ${appt.location}` : ''}
                  </BodyText>
                </div>
                <div className="text-sm">
                  <span
                    className={cn(
                      'px-2 py-1 rounded-full border',
                      isPast
                        ? isDark
                          ? 'bg-secondary-800 text-secondary-200 border-secondary-800'
                          : 'bg-secondary-100 text-secondary-700'
                        : isDark
                          ? 'bg-success-900/40 text-success-200 border-success-800'
                          : 'bg-success-100 text-success-800 border-success-200'
                    )}
                  >
                    {isPast ? 'Past' : 'Upcoming'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between" aria-label="appointment pagination">
        <Button variant="outline" size="sm" onClick={() => handlePrev()} disabled={loading || page <= 1} aria-label="Previous page">
          Previous
        </Button>
        <BodyText variant="muted" size="sm" aria-live="polite">
          Page {page} of {totalPages}
        </BodyText>
        <Button variant="outline" size="sm" onClick={() => handleNext()} disabled={loading || page >= totalPages} aria-label="Next page">
          Next
        </Button>
      </div>
    </Card>
  );
};

export default AppointmentHistoryCard;
